import {
  Option,
  Step,
  useZakeke,
  Group,
} from "@zakeke/zakeke-configurator-react";
import {
  T,
  useActualGroups,
  useUndoRedoActions,
  useUndoRegister,
} from "Helpers";
import { Map } from "immutable";
import { useEffect, useState } from "react";
import useStore from "Store";
import styled from "styled-components/macro";
import savedCompositionsIcon from "../../../assets/icons/saved_designs.svg";
import star from "../../../assets/icons/star.svg";
import noImage from "../../../assets/images/no_image.png";
import CustomSizesComponent from "../CustomSizesComponent";
import Designer from "../Designer";
import DesignsDraftList from "../DesignsDraftList";
import {
  AttributeName,
  SelectorMobileContainer,
  StepsMobileContainer,
  Template,
  TemplatesContainer,
} from "../LayoutStyled";
import Steps from "../Steps";
import { MenuItem, MobileItemsContainer } from "./SelectorMobileComponents";

const PriceInfoTextContainer = styled.div`
  font-size: 14px;
  padding: 0px 10px;
`;

// const sizeGroup: Group = {
//   id: -4,
//   guid: "00000000-0000-0000-0000-000000000001",
//   name: "Size",
//   enabled: true,
//   imageUrl: "",
//   icon: null,
//   attributes: [],
//   steps: [],
//   cameraLocationId: "",
//   displayOrder: 4,
//   direction: 1,
//   attributesAlwaysOpened: true,
// };

const SelectorMobile = () => {
  const {
    isSceneLoading,
    templates,
    currentTemplate,
    setCamera,
    setTemplate,
    sellerSettings,
    selectOption,
    draftCompositions,
    groups,
  } = useZakeke();
  const {
    selectedGroupId,
    setSelectedGroupId,
    selectedAttributeId,
    setSelectedAttributeId,
    selectedStepId,
    setSelectedStepId,
    isUndo,
    isRedo,
    setInitializeCustomSizes,
  } = useStore();
  const [scrollLeft, setScrollLeft] = useState<number | null>(null);
  const [optionsScroll, setOptionsScroll] = useState<number | null>(null);
  const [attributesScroll, setAttributesScroll] = useState<number | null>(null);
  const [isTemplateEditorOpened, setIsTemplateEditorOpened] = useState(false);
  const [isDesignsDraftListOpened, setisDesignsDraftListOpened] =
    useState(false);
  const [isStartRegistering, setIsStartRegistering] = useState(false);
  const undoRegistering = useUndoRegister();
  const undoRedoActions = useUndoRedoActions();

  const actualGroups = useActualGroups() ?? [];

  const selectedGroup = selectedGroupId
    ? actualGroups.find((group) => group.id === selectedGroupId)
    : null;
  const selectedStep = selectedGroupId
    ? actualGroups
        .find((group) => group.id === selectedGroupId)
        ?.steps.find((step) => step.id === selectedStepId)
    : null;
  const currentAttributes = selectedStep
    ? selectedStep.attributes
    : selectedGroup
    ? selectedGroup.attributes
    : [];
  const selectedAttribute = currentAttributes
    ? currentAttributes.find((attr) => attr.id === selectedAttributeId)
    : null;
  const options = selectedAttribute?.options ?? [];
  const groupIndex =
    actualGroups && selectedGroup ? actualGroups.indexOf(selectedGroup) : 0;

  const [lastSelectedSteps, setLastSelectedSteps] = useState(
    Map<number, number>()
  );

  const swap = (group: Group[], i: number, j: number) => {
    let temp = group[i];
    group[i] = group[j];
    group[j] = temp;
    return group;
  };

  if (actualGroups.length > 1)
    swap(actualGroups, actualGroups.length - 2, actualGroups.length - 1);

  const handleNextGroup = () => {
    if (selectedGroup) {
      if (groupIndex < actualGroups.length - 1) {
        const nextGroup = actualGroups[groupIndex + 1];
        handleGroupSelection(nextGroup.id);
      }
    }
  };

  const handlePreviousGroup = () => {
    if (selectedGroup) {
      if (groupIndex > 0) {
        let previousGroup = actualGroups[groupIndex - 1];
        handleGroupSelection(previousGroup.id);

        // Select the last step
        if (previousGroup.steps.length > 0)
          handleStepSelection(
            previousGroup.steps[previousGroup.steps.length - 1].id
          );
      }
    }
  };

  const handleStepChange = (step: Step | null) => {
    if (step) handleStepSelection(step.id);
  };

  const handleGroupSelection = (groupId: number | null) => {
    setIsStartRegistering(undoRegistering.startRegistering());

    if (groupId && selectedGroupId !== groupId && !isUndo && !isRedo) {
      undoRedoActions.eraseRedoStack();
      undoRedoActions.fillUndoStack({
        type: "group",
        id: selectedGroupId,
        direction: "undo",
      });
      undoRedoActions.fillUndoStack({
        type: "group",
        id: groupId,
        direction: "redo",
      });
    }

    setSelectedGroupId(groupId);
  };

  const handleStepSelection = (stepId: number | null) => {
    setIsStartRegistering(undoRegistering.startRegistering());

    if (selectedStepId !== stepId && !isUndo && !isRedo) {
      undoRedoActions.eraseRedoStack();
      undoRedoActions.fillUndoStack({
        type: "step",
        id: selectedStepId,
        direction: "undo",
      });
      undoRedoActions.fillUndoStack({
        type: "step",
        id: stepId ?? null,
        direction: "redo",
      });
    }

    setSelectedStepId(stepId);

    const newStepSelected = lastSelectedSteps.set(selectedGroupId!, stepId!);
    setLastSelectedSteps(newStepSelected);
  };

  const handleAttributeSelection = (attributeId: number) => {
    setIsStartRegistering(undoRegistering.startRegistering());

    if (
      attributeId &&
      selectedAttributeId !== attributeId &&
      !isUndo &&
      !isRedo
    ) {
      undoRedoActions.eraseRedoStack();
      undoRedoActions.fillUndoStack({
        type: "attribute",
        id: selectedAttributeId,
        direction: "undo",
      });
      undoRedoActions.fillUndoStack({
        type: "attribute",
        id: attributeId,
        direction: "redo",
      });
    }

    setSelectedAttributeId(attributeId);
  };

  const handleOptionSelection = (option: Option) => {
    if (option.attribute.name.toLocaleLowerCase() === "size category")
      setInitializeCustomSizes([]);
    const undo = undoRegistering.startRegistering();
    undoRedoActions.eraseRedoStack();
    undoRedoActions.fillUndoStack({
      type: "option",
      id: options.find((opt) => opt.selected)?.id ?? null,
      direction: "undo",
    });
    undoRedoActions.fillUndoStack({
      type: "option",
      id: option.id,
      direction: "redo",
    });

    setCamera(option.attribute.cameraLocationId!);
    if (option.name.toLowerCase() === "same as outer") {
      const selectedOuterOption = groups
        .find((group) => group.id === 4617)
        ?.attributes.find(
          (attr) => attr.code.toLowerCase() !== "fab" && attr.enabled
        )
        ?.options.find((opt) => opt.selected);

      const innerOptionToBeSelected = option.attribute.options.find(
        (opt) =>
          opt.name.toLowerCase() === selectedOuterOption?.name.toLowerCase()
      );

      if (innerOptionToBeSelected) selectOption(innerOptionToBeSelected.id);
    } else selectOption(option.id);
    if (option.attribute.id === 43930 || option.attribute.id === 56813) {
      const attributeID = groups
        .find((group) => group.id === option.attribute.groupId)
        ?.attributes.find(
          (attr) => attr.name.toLowerCase() === option.name.toLowerCase()
        )?.id;
      if (attributeID) {
        setSelectedAttributeId(null);
        handleAttributeSelection(attributeID);
      }
    }
    undoRegistering.endRegistering(undo);

    try {
      if ((window as any).algho) (window as any).algho.sendUserStopForm(true);
    } catch (e) {}
  };

  // Initial template selection
  useEffect(() => {
    if (templates.length > 0 && !currentTemplate) setTemplate(templates[0].id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  // Reset attribute selection when group selection changes
  useEffect(() => {
    if (selectedGroup && selectedGroup.id !== -2) {
      if (selectedGroup.steps.length > 0) {
        if (lastSelectedSteps.get(selectedGroupId!))
          handleStepSelection(lastSelectedSteps.get(selectedGroupId!)!);
        else {
          handleStepSelection(selectedGroup.steps[0].id);
          if (selectedGroup.steps[0].attributes.length === 1)
            handleAttributeSelection(selectedGroup.steps[0].attributes[0].id);
        }
      } else {
        handleStepSelection(null);
        if (selectedGroup.attributes.length === 1)
          handleAttributeSelection(selectedGroup.attributes[0].id);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup?.id]);

  useEffect(() => {
    if (selectedGroup?.id === -2) {
      setIsTemplateEditorOpened(true);
    }
  }, [selectedGroup?.id]);

  useEffect(() => {
    if (selectedGroup?.id === -3) {
      setisDesignsDraftListOpened(true);
    }
  }, [selectedGroup?.id]);

  // Camera
  useEffect(() => {
    if (!isSceneLoading && selectedGroup && selectedGroup.cameraLocationId) {
      setCamera(selectedGroup.cameraLocationId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup?.id, isSceneLoading]);

  useEffect(() => {
    if (selectedGroup && selectedGroup.steps.length > 0) {
      if (
        selectedGroup.steps.find((step) => step.id === selectedStep?.id) &&
        selectedGroup.steps.find((step) => step.id === selectedStep?.id)
          ?.attributes.length === 1
      )
        handleAttributeSelection(
          selectedGroup.steps!.find((step) => step.id === selectedStep?.id)!
            .attributes[0].id
        );
      else setSelectedAttributeId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStep?.id]);

  useEffect(() => {
    if (isStartRegistering) {
      undoRegistering.endRegistering(false);
      setIsStartRegistering(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStartRegistering]);

  return (
    <SelectorMobileContainer>
      {sellerSettings && sellerSettings.priceInfoText && (
        <PriceInfoTextContainer
          dangerouslySetInnerHTML={{ __html: sellerSettings.priceInfoText }}
        />
      )}

      {selectedGroup &&
        selectedGroup.id !== -2 &&
        selectedGroup.id !== -3 &&
        selectedGroup.id !== -4 &&
        selectedGroup.steps &&
        selectedGroup.steps.length > 0 && (
          <StepsMobileContainer>
            <Steps
              key={"steps-" + selectedGroup?.id}
              hasNextGroup={groupIndex !== actualGroups.length - 1}
              hasPreviousGroup={groupIndex !== 0}
              onNextStep={handleNextGroup}
              onPreviousStep={handlePreviousGroup}
              currentStep={selectedStep}
              steps={selectedGroup.steps}
              onStepChange={handleStepChange}
            />
          </StepsMobileContainer>
        )}
      {selectedGroup == null && (
        <MobileItemsContainer
          isLeftArrowVisible
          isRightArrowVisible
          scrollLeft={scrollLeft ?? 0}
          onScrollChange={(value) => setScrollLeft(value)}
        >
          {actualGroups.map((group) => {
            if (group)
              return (
                <MenuItem
                  key={group.guid}
                  imageUrl={
                    group.id === -3
                      ? savedCompositionsIcon
                      : group.imageUrl
                      ? group.imageUrl
                      : star
                  }
                  label={group.name ? group.name : T._("Customize", "Composer")}
                  onClick={() => handleGroupSelection(group.id)}
                ></MenuItem>
              );
            else return null;
          })}
        </MobileItemsContainer>
      )}

      {/* <AttributesContainer > */}
      {selectedGroup && selectedGroup.id === -2 && templates.length > 1 && (
        <TemplatesContainer>
          {templates.map((template) => (
            <Template
              key={template.id}
              selected={currentTemplate === template}
              onClick={() => {
                setTemplate(template.id);
              }}
            >
              {template.name}
            </Template>
          ))}
        </TemplatesContainer>
      )}
      {selectedGroup && (
        <MobileItemsContainer
          isLeftArrowVisible
          isRightArrowVisible
          scrollLeft={attributesScroll ?? 0}
          onScrollChange={(value) => setAttributesScroll(value)}
        >
          {/* Attributes */}
          {selectedGroup &&
            !selectedAttributeId &&
            currentAttributes &&
            currentAttributes.map((attribute) => {
              return (
                <MenuItem
                  selected={attribute.id === selectedAttributeId}
                  key={attribute.guid}
                  onClick={() => handleAttributeSelection(attribute.id)}
                  images={attribute.options
                    .slice(0, 4)
                    .map((x) => (x.imageUrl ? x.imageUrl : noImage))}
                  label={attribute.name}
                  isRound={attribute.optionShapeType === 2}
                >
                  <AttributeName>
                    {" "}
                    {attribute.name.toUpperCase()}{" "}
                  </AttributeName>
                </MenuItem>
              );
            })}

          {(selectedAttribute &&
            selectedAttribute.name.toLowerCase() === "size category") ||
          selectedGroup.name.toLowerCase() !== "size" ? (
            <MobileItemsContainer
              isLeftArrowVisible={options.length !== 0}
              isRightArrowVisible={options.length !== 0}
              scrollLeft={optionsScroll ?? 0}
              onScrollChange={(value) => setOptionsScroll(value)}
            >
              {selectedAttribute &&
                selectedAttribute.options.map(
                  (option) =>
                    option.enabled && (
                      <MenuItem
                        isRound={selectedAttribute.optionShapeType === 2}
                        description={option.description}
                        selected={option.selected}
                        imageUrl={option.imageUrl ?? ""}
                        label={option.name}
                        hideLabel={selectedAttribute.hideOptionsLabel}
                        key={option.guid}
                        onClick={() => handleOptionSelection(option)}
                      />
                    )
                )}
            </MobileItemsContainer>
          ) : (
            selectedAttribute && (
              <CustomSizesComponent
                attribute={selectedAttribute!}
                onCloseClick={() => {
                  setIsTemplateEditorOpened(false);
                  setSelectedAttributeId(null);
                }}
              />
            )
          )}
        </MobileItemsContainer>
      )}

      {/* Designer / Customizer */}
      {selectedGroup?.id === -2 && isTemplateEditorOpened && (
        <Designer
          onCloseClick={() => {
            setIsTemplateEditorOpened(false);
            handleGroupSelection(null);
          }}
        />
      )}

      {/* Saved Compositions */}
      {draftCompositions &&
        selectedGroup?.id === -3 &&
        isDesignsDraftListOpened && (
          <DesignsDraftList
            onCloseClick={() => {
              setIsTemplateEditorOpened(false);
              handleGroupSelection(null);
            }}
          />
        )}

      {/* <CustomSizesComponent
          attribute={selectedAttribute}
          onCloseClick={() => {
            setIsTemplateEditorOpened(false);
            setSelectedGroupId(null);
          }}
        />
      )} */}
    </SelectorMobileContainer>
  );
};

export default SelectorMobile;
